<script setup lang="ts">

// Imports
import { computed, ref, onMounted, getCurrentInstance } from 'vue'

// Props
type ButtonType = 'primary' | 'secondary' | 'tertiary' | 'text' | 'transparent'
type ButtonSize = 'small' | 'medium' | 'default' | 'large'

interface ButtonSettings {
  popupEnabled?: boolean | string | number
  linkType?: ButtonType
  linkSize?: ButtonSize
  linkIcon?: string
}

interface Props {
  url?: string
  title?: string
  ariaLabel?: string
  titleAttribute?: string
  target?: string
  popupEnabled?: boolean
  type?: ButtonType
  buttonSettings?: ButtonSettings | ButtonSettings[]
  bgColor?: string
  disabled?: boolean
  classes?: string
  size?: ButtonSize
  icon?: string
  arrowStyle?: 'default' | 'legacy'
  tabindex?: number | string
}

const props = withDefaults(defineProps<Props>(), {
  url: '',
  title: '',
  ariaLabel: '',
  titleAttribute: '',
  target: '',
  bgColor: '#000',
  disabled: false,
  classes: '',
  arrowStyle: 'default'
})

const pickSetting = <T>(explicit: T | undefined, fromSettings: T | undefined, fallback: T) => {
  if (explicit !== undefined && explicit !== null && explicit !== '') return explicit
  if (fromSettings !== undefined && fromSettings !== null && fromSettings !== '') return fromSettings
  return fallback
}

const unwrapSettings = (value: Props['buttonSettings']): ButtonSettings => {
  if (!value) return {}
  if (Array.isArray(value)) return value[0] || {}
  return value
}

const coerceEnabled = (value: unknown) =>
  value === true || value === 1 || value === '1' || value === 'true'

const instance = getCurrentInstance()
const hasExplicitPopupEnabled = () => {
  const raw = instance?.vnode.props
  if (!raw) return false
  return Object.prototype.hasOwnProperty.call(raw, 'popupEnabled')
    || Object.prototype.hasOwnProperty.call(raw, 'popup-enabled')
}

const settings = computed(() => unwrapSettings(props.buttonSettings))
const resolvedType = computed(() => pickSetting(props.type, settings.value.linkType, 'primary' as ButtonType))
const resolvedSize = computed(() => pickSetting(props.size, settings.value.linkSize, 'default' as ButtonSize))
const resolvedPopupEnabled = computed(() => {
  if (hasExplicitPopupEnabled()) {
    return coerceEnabled(props.popupEnabled)
  }
  return coerceEnabled(settings.value.popupEnabled)
})

const normalizedIcon = computed(() => {
  const icon = String(pickSetting(props.icon, settings.value.linkIcon, '')).trim()
  if (!icon || icon === 'none') return ''
  return icon
})

const iconType = computed<'none' | 'download' | 'play-video' | 'image'>(() => {
  if (!normalizedIcon.value) return 'none'
  if (normalizedIcon.value === 'download') return 'download'
  if (normalizedIcon.value === 'play-video') return 'play-video'
  return 'image'
})

// computedClasses: computed class for the button
const computedClasses = computed(() => {
  // Button types
  const typeClasses = {
    primary: ' btn-primary',
    secondary: ' btn-secondary',
    tertiary: ' btn-transparent',
    text: ' btn-text-style',
    transparent: ' btn-transparent'
  } satisfies Record<ButtonType, string>

  const sizeClasses = {
    small: ' --small',
    medium: ' --medium',
    default: '',
    large: ' --large'
  } satisfies Record<ButtonSize, string>

  const typeClass = typeClasses[resolvedType.value]
  const sizeClass = sizeClasses[resolvedSize.value]
  const popupClass = resolvedPopupEnabled.value ? ' --js-open-popup' : ''
  const iconClass = normalizedIcon.value ? ' --has-icon' : ''
  const arrowClass = props.arrowStyle === 'legacy' ? ' --legacy-arrow' : ''
  const additionalClasses = props.classes ? ` ${props.classes}` : ''
  
  return `btn${ typeClass }${ sizeClass }${ popupClass }${ iconClass }${ arrowClass }${ hasTransition.value ? ' has-transition' : '' } ${ additionalClasses }`
})

// Track if transition should be applied (after 3 seconds)
const hasTransition = ref(false)

onMounted(() => {
  setTimeout(() => {
    hasTransition.value = true
  }, 3000)
})

const handlePopupButtonClick = (event: MouseEvent) => {
  event.preventDefault()
  if (typeof window === 'undefined') return

  const popupWindow = window as Window & {
    __delightPendingPopupOpen?: { timestamp: number }
  }
  popupWindow.__delightPendingPopupOpen = {
    timestamp: Date.now()
  }

  window.dispatchEvent(new CustomEvent('popup:open'))
}

</script>

<!-- -------------------------------------------------------------------------------------------------
    Template
------------------------------------------------------------------------------------------------- -->

<template>
  <button
    v-if="title && resolvedPopupEnabled"
    type="button"
    :class="computedClasses"
    :disabled="disabled || undefined"
    :aria-label="ariaLabel || undefined"
    :title="titleAttribute || undefined"
    :tabindex="tabindex"
    @click="handlePopupButtonClick"
  >
    <span v-if="resolvedType !== 'text'" class="btn-bg"></span>
    <span class="btn-text">
      <img
        v-if="iconType === 'image'"
        :src="normalizedIcon"
        alt=""
        aria-hidden="true"
        loading="lazy"
        class="btn-icon-image"
      >
      <span v-else-if="iconType !== 'none'" class="btn-icon" :data-icon="iconType" aria-hidden="true">
        <svg v-if="iconType === 'download'" viewBox="0 0 20 20" fill="none" width="20" height="20">
          <path d="M10 3v11m0 0l-4-4m4 4l4-4M3 17h14" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
        <svg v-else-if="iconType === 'play-video'" viewBox="0 0 24 24" fill="none" width="20" height="20">
          <path d="M9.5 15.1263C9.5 15.7196 10.1564 16.078 10.6556 15.7571L15.5186 12.6309C15.9779 12.3357 15.9779 11.6643 15.5186 11.3691L10.6556 8.24287C10.1564 7.92199 9.5 8.28037 9.5 8.87375V15.1263Z" fill="currentColor"/>
          <circle cx="12" cy="12" r="9.25" stroke="currentColor" stroke-width="1.5"/>
        </svg>
      </span>
      {{ title }}
    </span>
    <span v-if="props.arrowStyle === 'legacy'" class="icon-text-after" aria-hidden="true"></span>
    <span v-else class="link-icon" aria-hidden="true">
      <svg width="12" height="9" viewBox="0 0 12 9" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M5.54681 9L9.12766 4.94043L0 4.94043V4.05958L9.12766 4.05957L5.54681 0H6.48511L12 4.5L6.48511 9H5.54681Z" fill="currentColor"/>
      </svg>
    </span>
  </button>

  <a 
    v-else-if="url && title"
    :href="disabled ? undefined : url"
    :target="target || undefined"
    :rel="target === '_blank' && /^https?:\/\//i.test(url) ? 'noopener noreferrer' : undefined"
    :class="[computedClasses, { 'is-disabled': disabled }]"
    :aria-label="ariaLabel || undefined"
    :title="titleAttribute || undefined"
    :tabindex="tabindex"
    :aria-disabled="disabled || undefined"
    @click="disabled && $event.preventDefault()"
  >
    <span v-if="resolvedType !== 'text'" class="btn-bg"></span>
    <span class="btn-text">
      <img
        v-if="iconType === 'image'"
        :src="normalizedIcon"
        alt=""
        aria-hidden="true"
        loading="lazy"
        class="btn-icon-image"
      >
      <span v-else-if="iconType !== 'none'" class="btn-icon" :data-icon="iconType" aria-hidden="true">
        <svg v-if="iconType === 'download'" viewBox="0 0 20 20" fill="none" width="20" height="20">
          <path d="M10 3v11m0 0l-4-4m4 4l4-4M3 17h14" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
        <svg v-else-if="iconType === 'play-video'" viewBox="0 0 24 24" fill="none" width="20" height="20">
          <path d="M9.5 15.1263C9.5 15.7196 10.1564 16.078 10.6556 15.7571L15.5186 12.6309C15.9779 12.3357 15.9779 11.6643 15.5186 11.3691L10.6556 8.24287C10.1564 7.92199 9.5 8.28037 9.5 8.87375V15.1263Z" fill="currentColor"/>
          <circle cx="12" cy="12" r="9.25" stroke="currentColor" stroke-width="1.5"/>
        </svg>
      </span>
      {{ title }}
    </span>
    <span v-if="props.arrowStyle === 'legacy'" class="icon-text-after" aria-hidden="true"></span>
    <span v-else class="link-icon" aria-hidden="true">
      <svg width="12" height="9" viewBox="0 0 12 9" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M5.54681 9L9.12766 4.94043L0 4.94043V4.05958L9.12766 4.05957L5.54681 0H6.48511L12 4.5L6.48511 9H5.54681Z" fill="currentColor"/>
      </svg>
    </span>
  </a>
</template>

<!-- -------------------------------------------------------------------------------------------------
    Styling
------------------------------------------------------------------------------------------------- -->

<style scoped>

.btn,
.btn-text {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  position: relative;
  transition: all 0.3s ease;
  white-space: nowrap;
}

.btn {
  cursor: pointer;
  flex-shrink: 0;
}

.btn:disabled,
.btn.is-disabled {
  cursor: not-allowed;
  pointer-events: none;
}

/* Primary button */
.btn-primary  {
  height: 48px;
  border-radius: 8px;
  overflow: hidden;
  padding-inline: 26px;
  color: var(--color-white);

  @media (min-width: 1024px) {
    height: 52px;
  }
}

.btn-primary .btn-bg {
  display: block;
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: var(--color-black);
}

.btn-primary.has-transition .btn-bg {
  transition: all .3s ease;
}

.btn-primary .btn-text {
  font-weight: 500;
  letter-spacing: -.32px;
  line-height: 140%;
  position: relative;
  z-index: 1;
  transition: all .3s ease;

  @media (min-width: 768px) {
    font-size: 18px;
  }
}

.btn-secondary {
  height: 48px;
  border-radius: 8px;
  overflow: hidden;
  padding-inline: 26px;
  color: var(--color-black);
  background: var(--color-white);
  border: 1px solid var(--color-black);

  @media (min-width: 1024px) {
    height: 52px;
  }
}

.btn-secondary .btn-bg {
  display: none;
}

.btn-secondary .btn-text {
  font-weight: 500;
  letter-spacing: -.32px;
  line-height: 140%;
  position: relative;
  z-index: 1;
  transition: all .3s ease;

  @media (min-width: 768px) {
    font-size: 18px;
  }
}

.btn-primary.--small,
.btn-secondary.--small,
.btn-transparent.--small {
  height: 30px;
  padding-inline: 12px;
}

.btn-primary.--small .btn-text,
.btn-secondary.--small .btn-text,
.btn-transparent.--small .btn-text {
  font-size: 14px;
}

.btn-primary.--medium,
.btn-secondary.--medium,
.btn-transparent.--medium {
  height: 42px;
  padding-inline: 12px;
}

.btn-primary.--medium .btn-text,
.btn-secondary.--medium .btn-text,
.btn-transparent.--medium .btn-text {
  font-size: 14px;
}

.btn-secondary:hover {
  background: var(--color-white);
}

.btn-icon,
.link-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.btn-icon {
  margin-right: 4px;
  flex-shrink: 0;
}

.btn-icon-image {
  width: 20px;
  height: 20px;
  margin-right: 4px;
  flex-shrink: 0;
  object-fit: contain;
}

.btn-primary .btn-icon-image {
  filter: brightness(0) invert(1);
}

.link-icon {
  opacity: 0;
  position: absolute;
  right: 26px;
  transition: all 0.3s ease;
  width: 0;
}

.--large .link-icon {
  right: 26px;
}

.--medium .link-icon,
.--small .link-icon {
  right: 12px;
}

.btn-primary.--legacy-arrow .icon-text-after,
.btn-secondary.--legacy-arrow .icon-text-after,
.btn-transparent.--legacy-arrow .icon-text-after {
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1;
  position: absolute;
  right: 14px;
  top: 50%;
  transform: translate(-13px, -50%);
  opacity: 0;
  transition: transform .3s ease, opacity .5s ease;
}

.btn-primary.--legacy-arrow.--small .icon-text-after,
.btn-secondary.--legacy-arrow.--small .icon-text-after,
.btn-transparent.--legacy-arrow.--small .icon-text-after {
  transform: translate(-13px, -50%) scale(0.8);
}

.btn-primary.--legacy-arrow .icon-text-after:before,
.btn-secondary.--legacy-arrow .icon-text-after:before,
.btn-transparent.--legacy-arrow .icon-text-after:before {
  background-color: currentColor;
  content: '';
  display: inline-block;
  height: 2px;
  width: 16px;
}

.btn-primary.--legacy-arrow .icon-text-after:after,
.btn-secondary.--legacy-arrow .icon-text-after:after,
.btn-transparent.--legacy-arrow .icon-text-after:after {
  border-right: 2px solid;
  border-top: 2px solid;
  content: '';
  display: inline-block;
  height: 10px;
  margin-left: -10px;
  transform: rotate(45deg);
  width: 10px;
}

.btn-primary:hover .btn-bg {
  opacity: 0.9;
}

.btn-transparent.--has-icon {
  transition: all .3s ease;
}

.btn-transparent.--has-icon:hover {
  background: rgba(0, 0, 0, 0.05);
}

.btn:not(.--legacy-arrow):not(:disabled):not(.is-disabled):hover .btn-text {
  transform: translate(-8px, 0);
}

.btn:not(.--legacy-arrow):not(:disabled):not(.is-disabled):hover .link-icon {
  opacity: 1;
  transform: translate(8px, 0);
  width: 12px;
}

.btn.--legacy-arrow:not(:disabled):not(.is-disabled):hover .btn-text {
  transform: translate(-13px, 0);
}

.btn.--legacy-arrow:not(:disabled):not(.is-disabled):hover .icon-text-after {
  opacity: 1;
  transform: translateY(-50%);
}

.btn.--legacy-arrow.--small:not(:disabled):not(.is-disabled):hover .icon-text-after {
  transform: translateY(-50%) scale(0.8);
}

/* Transparent button (primary sizing, no background) */
.btn-transparent {
  height: 48px;
  border-radius: 8px;
  overflow: hidden;
  padding-left: 25px;
  padding-right: 25px;
  border: 1px solid var(--color-black);
  color: var(--color-black);

  gap: 6px;

  @media (min-width: 1024px) {
    height: 52px;
  }
}

.btn-transparent .btn-bg {
  background: transparent !important;
  display: none;
}

.btn-transparent .btn-text {
  font-weight: 500;
  letter-spacing: -.32px;
  line-height: 140%;
  position: relative;
  z-index: 1;
  transition: all .3s ease;

  @media (min-width: 768px) {
    font-size: 18px;
    line-height: 138%; /* 24.84px */
    letter-spacing: -0.36px;
  }
}


/* Text style button */

.btn-text-style {
  color: var(--color-black);
}

.btn-text-style.--large {
  font-size: 16px;
  height: 22px;
  padding-left: 26px;
  padding-right: 26px;
}

@media (min-width: 1024px) {
  .btn-primary.--small,
  .btn-secondary.--small,
  .btn-transparent.--small {
    min-width: 102px;
  }

  .btn-primary.--medium,
  .btn-secondary.--medium,
  .btn-transparent.--medium {
    min-width: 128px;
  }

  .btn-primary.--large,
  .btn-secondary.--large,
  .btn-transparent.--large {
    min-width: 152px;
  }

}

</style>
